import { type HTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from './cn';
import type { CSSProperties } from 'react';

const badgeVariants = cva(
  'inline-flex items-center rounded-full font-medium',
  {
    variants: {
      variant: {
        info:      '',
        success:   '',
        warn:      '',
        danger:    '',
        progress:  '',
        neutral:   '',
        pending:   '',
        shipping:  '',
        delivered: '',
        cancelled: '',
        refunded:  '',
        paid:      '',
        failed:    '',
      },
      size: {
        sm: 'px-2 py-0.5 text-[11px]',
        md: 'px-2.5 py-0.5 text-xs',
      },
    },
    defaultVariants: { variant: 'neutral', size: 'md' },
  },
);

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

const BADGE_STYLES: Record<BadgeVariant, CSSProperties> = {
  info:      { background: 'var(--status-info-bg)',      color: 'var(--status-info-fg)' },
  success:   { background: 'var(--status-success-bg)',   color: 'var(--status-success-fg)' },
  warn:      { background: 'var(--status-warn-bg)',      color: 'var(--status-warn-fg)' },
  danger:    { background: 'var(--status-danger-bg)',    color: 'var(--status-danger-fg)' },
  progress:  { background: 'var(--status-progress-bg)',  color: 'var(--status-progress-fg)' },
  neutral:   { background: 'var(--status-neutral-bg)',   color: 'var(--status-neutral-fg)' },
  pending:   { background: 'var(--status-pending-bg)',   color: 'var(--status-pending-fg)' },
  shipping:  { background: 'var(--status-shipping-bg)',  color: 'var(--status-shipping-fg)' },
  delivered: { background: 'var(--status-delivered-bg)', color: 'var(--status-delivered-fg)' },
  cancelled: { background: 'var(--status-cancelled-bg)', color: 'var(--status-cancelled-fg)' },
  refunded:  { background: 'var(--status-refunded-bg)',  color: 'var(--status-refunded-fg)' },
  paid:      { background: 'var(--status-paid-bg)',      color: 'var(--status-paid-fg)' },
  failed:    { background: 'var(--status-failed-bg)',    color: 'var(--status-failed-fg)' },
};

export interface BadgeProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof badgeVariants> {}

export const Badge = ({ className, variant, size, style, ...props }: BadgeProps) => {
  const resolvedVariant = (variant ?? 'neutral') as BadgeVariant;
  return (
    <span
      className={cn(badgeVariants({ variant, size }), className)}
      style={{ ...BADGE_STYLES[resolvedVariant], ...style }}
      {...props}
    />
  );
};
